import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { type ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import { isSlackFederatedByConnectorSyncType } from './is-slack-federated-by-connector-sync-type';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getRequestedBackendExperiment } from './get-requested-backend-experiment';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isSlackFederatedByConnectorSyncType } from './is-slack-federated-by-connector-sync-type';

/**
 * Determines whether federated Slack search is enabled, using the connector status
 * transition feature flag when available, and falling back to the legacy experiment
 * and feature flag logic otherwise.
 *
 * @param selectedProduct - The currently selected product (string, or undefined for all products)
 * @param selectedProducts - Array of selected products (takes precedence over selectedProduct if provided)
 * @param options - Options for getting the experiment value
 * @param thirdPartyConfigs - Optional third party configs for auth check and connector source info
 * @returns boolean - Whether federated Slack search is enabled
 */
export const serveFederatedSlackExperiment = (
	selectedProduct?: string,
	selectedProducts?: string[],
	options: GetExperimentValueOptions<string> = {
		fireExperimentExposure: false,
	},
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): boolean => {
	// Determine effective selected product:
	// - If selectedProducts array is provided and non-empty, use it to determine the product
	// - Otherwise fall back to selectedProduct
	const effectiveSelectedProduct =
		selectedProducts && selectedProducts.length > 0
			? selectedProducts.includes('slack')
				? 'slack'
				: selectedProducts[0]
			: selectedProduct;

	// Only serve the experiment when user is under all product search or Slack product search
	const isSlackOrAllProducts = !effectiveSelectedProduct || effectiveSelectedProduct === 'slack';
	if (!isSlackOrAllProducts) {
		return false;
	}

	// Return false if Slack is not authorized (skip check if thirdPartyConfigs not provided)
	if (thirdPartyConfigs) {
		const isSlackAuthorized = thirdPartyConfigs.slack?.userNeedsOAuth === false;
		if (!isSlackAuthorized) {
			return false;
		}
	}

	// When the connector status transition FF is enabled, use the actual connector sync type
	// to determine if Slack should use federated search behavior
	if (fg('ai_3p_slack_connector_status_transition_enabled')) {
		return isSlackFederatedByConnectorSyncType(thirdPartyConfigs);
	}

	// Fallback to legacy experiment and feature flag logic
	const experimentValue = FeatureGates.getExperimentValue<string>(
		'ai-3p-slack-federated-search-analysis',
		'expValue',
		'legacy',
		options,
	);
	return (
		experimentValue === 'fed' ||
		experimentValue === 'fed-optimized' ||
		fg('ai_3p_slack_federated_search_enabled')
	);
};
